import React, { useState, useRef, useEffect } from "react";
import {
  X,
  Upload,
  Search,
  Check,
  ChevronDown,
  FileSpreadsheet,
  AlertCircle,
} from "lucide-react";
import GenerateTemplateConfiguration from "./GenerateTemplateConfiguration";
import {
  DocumentFileMap,
  ComponentInputs,
  SingleTemplate,
} from "../../models/whatsappTemplate.model";
import { useDispatch, useSelector } from "react-redux";
import { RootDispatch, RootState } from "../../store";
import {
  fetchWhatsappNumbers,
  fetchWhatsappTemplateById,
} from "../../store/slices/whatsappDataSlice";
import {
  createCampaign,
  editCampaign,
} from "../../store/slices/whatsappCampaignSlice";
import ResponseModel from "../../models/response.model";
import WhatsappCampaign from "../../models/whatsappCampaigns/campaign.model";

interface TemplateComponent {
  type: string;
  format?: string;
  text?: string;
  example?: {
    header_handle?: string[];
  };
}

export interface WhatsappTemplate {
  id: string;
  name: string;
  language: string;
  category: string;
  status: string;
  components: TemplateComponent[];
}

interface CreateCampaignModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialCampaign?: WhatsappCampaign;
}

export const CreateCampaignModal: React.FC<CreateCampaignModalProps> = ({
  isOpen,
  onClose,
  initialCampaign,
}) => {
  const dispatch: RootDispatch = useDispatch();
  // Form state
  const [name, setName] = useState("");
  const [selectedPhoneId, setSelectedPhoneId] = useState("");
  const [selectedTemplate, setSelectedTemplate] =
    useState<WhatsappTemplate | null>(null);
  const [dailyCap, setDailyCap] = useState("900");
  const [delayMs, setDelayMs] = useState("1000");
  const [maxAttempts, setMaxAttempts] = useState("3");
  const [file, setFile] = useState<File | null>(null);

  const [documentFile, setDocumentFile] = useState<DocumentFileMap>({});
  const [componentInputs, setComponentInputs] = useState<ComponentInputs>({});
  const [, setTemplateConfig] = useState<any>({});
  const templateInputRef = useRef<HTMLInputElement | null>(null);
  // const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isReplace, setIsReplace] = useState(false);
  const [selectedRunTime, setSelectedRunTime] = useState<string>("");

  // Searchable dropdown state
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);

  const templates: WhatsappTemplate[] = useSelector(
    (state: RootState) => state.whatsAppData.templates,
  );

  const singleTemplate: SingleTemplate = useSelector(
    (state: RootState) => state.whatsAppData.singleTemplate,
  );

  const phoneNumbers = useSelector(
    (state: RootState) => state.whatsAppData.phoneNumbers,
  );
  const isSubmitting = useSelector((state: RootState) =>
    initialCampaign
      ? state.whatsappCampaign.loading.editCampaign
      : state.whatsappCampaign.loading.createCampaign,
  );

  // intentionally not using whatsappCampaign.loading here

  useEffect(() => {
    dispatch(fetchWhatsappNumbers());
  }, [dispatch]);

  // Preload fields when editing an existing campaign
  useEffect(() => {
    if (!initialCampaign) {
      // reset to defaults when not editing
      setName("");
      setSelectedPhoneId("");
      setSelectedTemplate(null);
      setDailyCap("900");
      setDelayMs("1000");
      setMaxAttempts("3");
      setFile(null);
      setDocumentFile({});
      setComponentInputs({});
      setSelectedRunTime("10:00");
      return;
    }

    // Populate from provided campaign object (best-effort)
    try {
      setName(initialCampaign.name || "");

      // Settings
      setDailyCap((initialCampaign.settings?.dailyCap ?? "900").toString());
      setDelayMs((initialCampaign.settings?.delayMs ?? "1000").toString());
      setMaxAttempts((initialCampaign.settings?.maxAttempts ?? "3").toString());

      // Phone selection: prefer id, fallback to matching display number
      const phoneIdCandidate = initialCampaign.whatsapp?.phoneNumberId;
      if (phoneIdCandidate) {
        setSelectedPhoneId(phoneIdCandidate);
      } else if (phoneNumbers && phoneNumbers.length > 0) {
        const matched = phoneNumbers.find(
          (p: any) =>
            p.display_phone_number === initialCampaign.whatsapp?.phoneNumber,
        );
        if (matched) setSelectedPhoneId(matched.id);
      }

      // Template: find by name+language among available templates
      const foundTemplate = templates.find(
        (t) =>
          t.name === initialCampaign.whatsapp?.templateName &&
          t.language === initialCampaign.whatsapp?.templateLanguage,
      );
      if (foundTemplate) {
        setSelectedTemplate(foundTemplate as any);
        // populate singleTemplate details in redux
        try {
          dispatch(fetchWhatsappTemplateById(foundTemplate.id));
        } catch (e) {
          // ignore
        }
      } else {
        // leave selectedTemplate null if not found
        setSelectedTemplate(null);
      }

      // Component inputs from stored campaign values
      const newInputs: any = {};
      (initialCampaign.whatsapp?.headers || []).forEach(
        (val: any, idx: number) => {
          newInputs[`HEADER_${idx + 1}`] = val;
        },
      );
      (initialCampaign.whatsapp?.body || []).forEach(
        (val: any, idx: number) => {
          newInputs[`BODY_${idx + 1}`] = val;
        },
      );
      (initialCampaign.whatsapp?.footer || []).forEach(
        (val: any, idx: number) => {
          newInputs[`FOOTER_${idx + 1}`] = val;
        },
      );
      setComponentInputs(newInputs);

      // Document/image header if stored URL present
      const imageUrl = initialCampaign.whatsapp?.imageUrl;

      // Prefill run time if present in executionMeta
      const nextRun = initialCampaign.executionMeta?.nextRunAt;
      if (nextRun) {
        try {
          const parsed = new Date(nextRun as any);
          if (!isNaN(parsed.getTime())) {
            const hh = String(parsed.getHours()).padStart(2, "0");
            const mm = String(parsed.getMinutes()).padStart(2, "0");
            setSelectedRunTime(`${hh}:${mm}`);
          }
        } catch (e) {
          // ignore
        }
      }

      if (imageUrl) {
        const mockFile: any = {
          name: imageUrl.split("/").pop() || "asset",
          url: imageUrl,
          isStored: true,
        };
        setDocumentFile((prev) => ({ ...prev, HEADER: mockFile }));
      }
    } catch (err) {
      // swallow errors - best-effort prefill
    }
  }, [initialCampaign, templates, phoneNumbers, dispatch]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Filter templates based on search
  const filteredTemplates = templates.filter(
    (t) =>
      t.status === "APPROVED" &&
      (t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        t.category.toLowerCase().includes(searchQuery.toLowerCase())),
  );

  // Auto-fill header image URL if header component has sample media
  const handleSelectTemplate = (template: WhatsappTemplate) => {
    setSelectedTemplate(template);
    setIsDropdownOpen(false);
    setSearchQuery("");
    setComponentInputs({});
    setDocumentFile({});
    // Dispatch to populate detailed template (singleTemplate) in redux
    try {
      dispatch(fetchWhatsappTemplateById(template.id));
    } catch (err) {
      // ignore - best effort
    }
    const headerComp = template.components.find((c) => c.type === "HEADER");
    if (!headerComp) return;
  };
  // Helper function to convert File to Base64
  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        // Remove data URL prefix (e.g., "data:application/vnd.openxmlformats-officedocument...;base64,")
        const base64String = (reader.result as string).split(",")[1];
        resolve(base64String);
      };
      reader.onerror = (err) => reject(err);
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!selectedTemplate) {
      setError("Please select a WhatsApp template");
      return;
    }
    if (!selectedPhoneId) {
      setError("Please select a sender phone number");
      return;
    }
    // Excel file required when creating, or when editing with Replace checked
    if (!file && (!initialCampaign || isReplace)) {
      setError("Please upload an Excel contact file");
      return;
    }

    const selectedPhoneObj = phoneNumbers.find(
      (p: any) => p.id === selectedPhoneId,
    );

    // Fix order in handleSubmit:
    const formData = new FormData();

    // 1. Text Fields FIRST
    formData.append("name", name);
    formData.append("phoneNumberId", selectedPhoneId);
    formData.append(
      "phoneNumber",
      selectedPhoneObj?.display_phone_number || "",
    );
    formData.append("templateName", selectedTemplate.name);
    formData.append("templateLanguage", selectedTemplate.language);
    formData.append("dailyCap", dailyCap);
    formData.append("batchSize", dailyCap);
    formData.append("delayMs", delayMs);
    formData.append("maxAttempts", maxAttempts);

    const validKeys = new Set<string>();
    if (singleTemplate?.template?.components) {
      singleTemplate.template.components.forEach((comp) => {
        const type = comp.type.toUpperCase();
        const matches = (comp.text || "").match(/{{\s*\d+\s*}}/g) || [];
        matches.forEach((_, idx) => {
          validKeys.add(`${type}_${idx + 1}`);
        });
      });
    }

    const headers: string[] = [];
    const body: string[] = [];
    const footer: string[] = [];

    // Only pick values for keys valid in the current template
    Object.keys(componentInputs).forEach((key) => {
      if (!validKeys.has(key)) return; // Skip old persistent values

      const val = componentInputs[key];
      if (!val) return;
      if (key.startsWith("HEADER_")) headers.push(val);
      else if (key.startsWith("BODY_")) body.push(val);
      else if (key.startsWith("FOOTER_")) footer.push(val);
    });
    // ALWAYS append array markers so the backend knows to overwrite old values
    if (headers.length > 0) {
      headers.forEach((h) => formData.append("header", h)); //[cite: 1]
    } else {
      // Option A: Pass an empty string so multer/req.body registers 'header' as empty
      formData.append("header", "");
    }

    if (body.length > 0) {
      body.forEach((b) => formData.append("body", b)); //[cite: 1]
    } else {
      formData.append("body", "");
    }

    if (footer.length > 0) {
      footer.forEach((f) => formData.append("footer", f)); //[cite: 1]
    } else {
      formData.append("footer", "");
    }

    // Clear header media/imageUrl if the new template has no media header component
    const hasMediaHeader = singleTemplate?.template?.components?.some(
      (c) =>
        c.type === "HEADER" &&
        (c.format === "IMAGE" || c.format === "DOCUMENT"),
    );

    // 2. File fields LAST
    if (isReplace) {
      formData.append("isReplace", "true");
    }
    if (file) {
      const excelBase64 = await fileToBase64(file);
      formData.append("excelBase64", excelBase64); // Excel contact sheet
    }

    // Append nextRunAt if user selected a run time
    if (selectedRunTime) {
      try {
        const [hh, mm] = selectedRunTime.split(":").map((v) => parseInt(v, 10));
        let nextRunIso: string;

        const makeNextOccurrenceIso = (hours: number, minutes: number) => {
          const d = new Date();
          d.setHours(hours, minutes, 0, 0);
          // If the time for today has already passed or is equal to now, schedule for next day
          if (d.getTime() <= Date.now()) {
            d.setDate(d.getDate() + 1);
          }
          return d.toISOString();
        };

        // If editing and original nextRunAt exists, preserve the original full date-time
        // only if it still lies in the future and matches the selected time. Otherwise
        // compute the next occurrence (tomorrow if today's time has passed).
        const origNextRun = initialCampaign?.executionMeta?.nextRunAt;
        if (initialCampaign && origNextRun) {
          try {
            const origDate = new Date(origNextRun as any);
            if (!isNaN(origDate.getTime())) {
              const origH = String(origDate.getHours()).padStart(2, "0");
              const origM = String(origDate.getMinutes()).padStart(2, "0");
              const origTime = `${origH}:${origM}`;
              if (
                origTime === selectedRunTime &&
                origDate.getTime() > Date.now()
              ) {
                nextRunIso = origDate.toISOString();
              } else {
                nextRunIso = makeNextOccurrenceIso(hh, mm);
              }
            } else {
              nextRunIso = makeNextOccurrenceIso(hh, mm);
            }
          } catch (e) {
            nextRunIso = makeNextOccurrenceIso(hh, mm);
          }
        } else {
          nextRunIso = makeNextOccurrenceIso(hh, mm);
        }

        formData.append("nextRunAt", nextRunIso);
      } catch (e) {
        // ignore malformed time
      }
    }

    if (hasMediaHeader) {
      Object.keys(documentFile).forEach((k) => {
        const f: any = (documentFile as any)[k];
        if (!f || f.isStored) return;
        if (f instanceof File) {
          formData.append("files", f); // Additional uploaded assets
        }
      });
    } else {
      formData.append("files", ""); // Clear any existing media header if the new template has no media header
    }
    try {
      let res: ResponseModel;
      if (initialCampaign) {
        // editing existing campaign
        formData.append("id", initialCampaign._id);
        res = await dispatch(
          editCampaign({
            campaignId: initialCampaign._id,
            campaignData: formData,
          }),
        ).unwrap();
      } else {
        res = await dispatch(
          createCampaign({ campaignData: formData }),
        ).unwrap();
      }
      if (res.success) {
        onClose();
      }
    } catch (err: any) {
      setError(err.message || "Failed to create campaign");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center overflow-y-auto bg-slate-900/60 p-4 backdrop-blur-sm sm:p-6">
      <div className="relative w-full max-w-2xl rounded-2xl bg-white shadow-2xl transition-all max-h-[90vh] overflow-auto">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-bold text-slate-800">
                {initialCampaign
                  ? "Edit WhatsApp Campaign"
                  : "Create WhatsApp Campaign"}
              </h2>
              {initialCampaign && (
                <span className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">
                  Editing
                </span>
              )}
            </div>
            <p className="text-xs text-slate-500">
              Set up auto-sending campaign queue with contact sheet
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body Form */}
        <form
          onSubmit={handleSubmit}
          className="p-6 space-y-5 max-h-[70vh] overflow-auto"
        >
          {/* Campaign Name */}
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600">
              Campaign Name
            </label>
            <input
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Vadodara Solar Dealers Q3"
              className="mt-1.5 w-full rounded-xl border border-slate-200 bg-slate-50/50 px-4 py-2.5 text-sm text-slate-800 outline-none transition focus:border-green-500 focus:bg-white focus:ring-2 focus:ring-green-500/20"
            />
          </div>

          {/* Sender Phone Number */}
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600">
              Sender WhatsApp Number
            </label>
            <select
              required
              value={selectedPhoneId}
              onChange={(e) => setSelectedPhoneId(e.target.value)}
              className="mt-1.5 w-full rounded-xl border border-slate-200 bg-slate-50/50 px-4 py-2.5 text-sm text-slate-800 outline-none transition focus:border-green-500 focus:bg-white focus:ring-2 focus:ring-green-500/20"
            >
              <option value="">Select Phone Number</option>
              {phoneNumbers.map((p: any) => (
                <option key={p.id} value={p.id}>
                  {p.verified_name} ({p.display_phone_number})
                </option>
              ))}
            </select>
          </div>

          {/* Searchable Template Selector */}
          <div className="relative" ref={dropdownRef}>
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600">
              Select Template
            </label>
            <div
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="mt-1.5 flex cursor-pointer items-center justify-between rounded-xl border border-slate-200 bg-slate-50/50 px-4 py-2.5 text-sm text-slate-800 transition hover:border-slate-300"
            >
              <span>
                {selectedTemplate
                  ? `${selectedTemplate.name} (${selectedTemplate.language})`
                  : "Choose an approved template..."}
              </span>
              <ChevronDown className="h-4 w-4 text-slate-400" />
            </div>

            {/* Dropdown Menu */}
            {isDropdownOpen && (
              <div className="absolute left-0 right-0 z-20 mt-2 max-h-60 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl">
                <div className="flex items-center border-b border-slate-100 px-3">
                  <Search className="h-4 w-4 text-slate-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search templates..."
                    className="w-full bg-transparent px-3 py-2 text-sm text-slate-800 outline-none"
                  />
                </div>
                <div className="max-h-48 overflow-y-auto p-1">
                  {filteredTemplates.length > 0 ? (
                    filteredTemplates.map((t) => (
                      <div
                        key={t.id}
                        onClick={() => handleSelectTemplate(t)}
                        className="flex cursor-pointer items-center justify-between rounded-lg px-3 py-2 text-sm text-slate-700 hover:bg-slate-100"
                      >
                        <div>
                          <p className="font-medium">{t.name}</p>
                          <p className="text-xs text-slate-400">
                            {t.category} • {t.language}
                          </p>
                        </div>
                        {selectedTemplate?.id === t.id && (
                          <Check className="h-4 w-4 text-green-500" />
                        )}
                      </div>
                    ))
                  ) : (
                    <p className="p-3 text-center text-xs text-slate-400">
                      No matching templates found
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Template Configuration Component (dynamic params & file uploads) */}
          {selectedTemplate && (
            <div className="mt-3">
              <GenerateTemplateConfiguration
                singleTemplate={singleTemplate}
                setComponentInputs={setComponentInputs}
                componentInputs={componentInputs}
                documentFile={documentFile}
                setDocumentFile={setDocumentFile}
                handleSelectFile={() => {
                  if (templateInputRef.current) {
                    templateInputRef.current.value = "";
                    templateInputRef.current.click();
                  }
                }}
                inputRef={templateInputRef}
                setConfig={setTemplateConfig}
              />
            </div>
          )}

          {/* Run time selector (08:30 - 19:00 in 30m increments) */}
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600">
              Select Run Time
            </label>
            <select
              value={selectedRunTime}
              onChange={(e) => setSelectedRunTime(e.target.value)}
              className="mt-1.5 w-full rounded-xl border border-slate-200 bg-slate-50/50 px-4 py-2.5 text-sm text-slate-800 outline-none transition focus:border-green-500 focus:bg-white focus:ring-2 focus:ring-green-500/20"
            >
              <option value="">Select time (optional)</option>
              {(() => {
                const opts: JSX.Element[] = [];
                const start = 8 * 60 + 30; // 8:30 in minutes
                const end = 19 * 60; // 19:00
                for (let m = start; m <= end; m += 30) {
                  const hh = Math.floor(m / 60);
                  const mm = m % 60;
                  const value = `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
                  const ampm = hh >= 12 ? "PM" : "AM";
                  const dispH = ((hh + 11) % 12) + 1;
                  const label = `${dispH}:${String(mm).padStart(2, "0")} ${ampm}`;
                  opts.push(
                    <option key={value} value={value}>
                      {label}
                    </option>,
                  );
                }
                return opts;
              })()}
            </select>
          </div>

          {/* Header image handled by GenerateTemplateConfiguration */}

          {/* Daily Cap */}
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600">
              Daily Message Cap
            </label>
            <input
              type="number"
              value={dailyCap}
              onChange={(e) => setDailyCap(e.target.value)}
              className="mt-1.5 w-full rounded-xl border border-slate-200 bg-slate-50/50 px-4 py-2.5 text-sm text-slate-800 outline-none focus:border-green-500 focus:bg-white focus:ring-2 focus:ring-green-500/20"
            />
          </div>

          {/* Delay (ms) */}
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600">
              Message Delay (ms)
            </label>
            <input
              type="number"
              min={0}
              value={delayMs}
              onChange={(e) => setDelayMs(e.target.value)}
              className="mt-1.5 w-full rounded-xl border border-slate-200 bg-slate-50/50 px-4 py-2.5 text-sm text-slate-800 outline-none focus:border-green-500 focus:bg-white focus:ring-2 focus:ring-green-500/20"
            />
          </div>

          {/* Max Attempts */}
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600">
              Max Attempts
            </label>
            <input
              type="number"
              min={1}
              value={maxAttempts}
              onChange={(e) => setMaxAttempts(e.target.value)}
              className="mt-1.5 w-full rounded-xl border border-slate-200 bg-slate-50/50 px-4 py-2.5 text-sm text-slate-800 outline-none focus:border-green-500 focus:bg-white focus:ring-2 focus:ring-green-500/20"
            />
          </div>

          {/* File Upload Dropzone */}
          {/* Replace existing contacts toggle (visible only when editing) */}
          {initialCampaign && (
            <div className="flex items-center gap-3">
              <label className="inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={isReplace}
                  onChange={(e) => setIsReplace(e.target.checked)}
                  className="mr-2 h-4 w-4 rounded border-slate-200 text-emerald-600 focus:ring-0"
                />
                <span className="text-sm text-slate-700">
                  Replace existing contacts
                </span>
              </label>
            </div>
          )}
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600">
              Upload Excel Sheet (.xlsx, .xls)
            </label>
            <div className="relative mt-1.5 flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-200 bg-slate-50/50 p-6 transition hover:border-slate-300">
              <input
                type="file"
                accept=".xlsx, .xls"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="absolute inset-0 cursor-pointer opacity-0"
              />
              {file ? (
                <div className="flex items-center gap-2 text-green-600">
                  <FileSpreadsheet className="h-6 w-6" />
                  <span className="text-sm font-medium">{file.name}</span>
                </div>
              ) : (
                <div className="text-center">
                  <Upload className="mx-auto h-8 w-8 text-slate-400" />
                  <p className="mt-2 text-xs font-medium text-slate-600">
                    Click or drag Excel sheet here
                  </p>
                  <p className="text-[10px] text-slate-400">
                    Must contain columns like mobileNumber, contactPersonName
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Footer Actions */}
          {error && (
            <div className="flex items-center gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-600">
              <AlertCircle className="h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl px-5 py-2.5 text-xs font-semibold text-slate-600 hover:bg-slate-100"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="rounded-xl bg-emerald-600 px-6 py-2.5 text-xs font-semibold text-white shadow-lg shadow-emerald-600/20 hover:bg-emerald-700 disabled:opacity-50"
            >
              {isSubmitting
                ? initialCampaign
                  ? "Updating..."
                  : "Creating..."
                : initialCampaign
                  ? "Update Campaign"
                  : "Start Campaign Queue"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
